import React, { useEffect, useState } from 'react'
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Stack,
  TextField,
  Typography,
  Select,
  ListSubheader,
  MenuItem,
  FormControl,
  InputLabel
} from '@mui/material'
import { LocalizationProvider, DesktopDatePicker } from '@mui/x-date-pickers'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import Visibility from '@mui/icons-material/Visibility'
import VisibilityOff from '@mui/icons-material/VisibilityOff'
import { formatDate } from '../../../../Utils/Format'
import { useTranslation } from 'react-i18next'
import { listSuggestionsByClientId } from '../../../../services/alerts'
import DocAlertRecipientsForm from '../Alerts/DocAlertRecipientsForm'

const ACTAS_SUBCATEGORY_ID = 140

const buildActaFilename = (fields, t) => {
  const { tipo_libro, numero_acta, numero_tomo, fecha_libro } = fields
  if (!tipo_libro) return ''
  return `${tipo_libro}, acta ${numero_acta ?? ''}, tomo ${numero_tomo ?? ''}, ${
    fecha_libro
      ? formatDate(
          t,
          fecha_libro instanceof Date ? fecha_libro : fecha_libro.$d
        )
      : ''
  }`
}

const ConfigurationDialog = ({
  open,
  files,
  handleBack,
  handleUpload,
  selectedSubcategoryId,
  notifyClientId
}) => {
  const { t } = useTranslation()
  // Initialize state as an array of objects, one for each file
  const [config, setConfig] = useState([])
  const isActa = selectedSubcategoryId === ACTAS_SUBCATEGORY_ID

  // Batch-level notify option — one notification for the whole upload, not per file
  const [notify, setNotify] = useState(false)
  const [recipients, setRecipients] = useState([])
  const [suggestions, setSuggestions] = useState([])

  useEffect(() => {
    setConfig(
      files.map((file) => ({
        filename:
          file.name.substring(0, file.name.lastIndexOf('.')) || file.name,
        notes: '',
        hasExpiration: false,
        expiration: null,
        visible: true,
        originalFile: file,
        // Acta fields (only used when isActa)
        tipo_libro: '',
        numero_acta: '',
        numero_tomo: '',
        fecha_libro: null
      }))
    )
  }, [files])

  useEffect(() => {
    if (!open) {
      setNotify(false)
      setRecipients([])
    }
  }, [open])

  useEffect(() => {
    if (open && notify && notifyClientId && suggestions.length === 0) {
      listSuggestionsByClientId(notifyClientId).then((res) =>
        setSuggestions(Array.isArray(res) ? res : [])
      )
    }
  }, [open, notify, notifyClientId, suggestions.length])

  const handleAddRecipient = (recipient) => {
    setRecipients((prev) => [...prev, recipient])
  }

  const handleRemoveRecipient = (localId) => {
    setRecipients((prev) => prev.filter((r) => r._local_id !== localId))
  }

  const updateFileConfig = (index, key, value) => {
    setConfig((prev) => {
      const next = [...prev]
      next[index] = { ...next[index], [key]: value }

      // If acta field changed, auto-rebuild the filename
      if (
        isActa &&
        ['tipo_libro', 'numero_acta', 'numero_tomo', 'fecha_libro'].includes(
          key
        )
      ) {
        next[index].filename = buildActaFilename(next[index], t)
      }

      return next
    })
  }

  const isUploadDisabled = config.some((item) => {
    if (!item.filename?.trim()) return true
    if (isActa) {
      return (
        !item.tipo_libro ||
        !item.numero_acta ||
        !item.numero_tomo ||
        !item.fecha_libro
      )
    }
    return false
  })

  return (
    <Dialog open={open} fullWidth maxWidth='md'>
      <DialogTitle>Step 2: Configure Documents</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={4} sx={{ my: 1 }}>
          {config.map((item, index) => (
            <Box
              key={index}
              sx={{
                p: 2,
                pb: 1,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1
              }}
            >
              <Typography variant='subtitle2' color='primary' gutterBottom>
                FILE: {item.originalFile.name}
              </Typography>

              {/* Acta fields — only shown for subcategory 140 */}
              {isActa && (
                <Stack spacing={2} sx={{ mt: 2 }}>
                  <FormControl fullWidth size='small'>
                    <InputLabel>Tipo de libro</InputLabel>
                    <Select
                      value={item.tipo_libro}
                      label='Tipo de libro'
                      onChange={(e) =>
                        updateFileConfig(index, 'tipo_libro', e.target.value)
                      }
                    >
                      <ListSubheader>Sociedades Anónimas</ListSubheader>
                      <MenuItem value='Libro de Actas Junta Directiva'>
                        Libro de Actas Junta Directiva
                      </MenuItem>
                      <MenuItem value='Libro de Actas de Asamblea de Socios'>
                        Libro de Actas de Asamblea de Socios
                      </MenuItem>
                      <MenuItem value='Libro de Registro de Accionistas'>
                        Libro de Registro de Accionistas
                      </MenuItem>
                      <ListSubheader>
                        Sociedades de Responsabilidad Limitada
                      </ListSubheader>
                      <MenuItem value='Libro de Actas de Asamblea de Cuotistas'>
                        Libro de Actas de Asamblea de Cuotistas
                      </MenuItem>
                      <MenuItem value='Libro de Registro de Cuotistas'>
                        Libro de Registro de Cuotistas
                      </MenuItem>
                    </Select>
                  </FormControl>

                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                    <TextField
                      label='Número de Acta'
                      size='small'
                      fullWidth
                      type='number'
                      value={item.numero_acta}
                      onChange={(e) =>
                        updateFileConfig(index, 'numero_acta', e.target.value)
                      }
                    />
                    <TextField
                      label='Número de Tomo'
                      size='small'
                      fullWidth
                      type='number'
                      value={item.numero_tomo}
                      onChange={(e) =>
                        updateFileConfig(index, 'numero_tomo', e.target.value)
                      }
                    />
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <DesktopDatePicker
                        label='Fecha de libro'
                        value={item.fecha_libro}
                        onChange={(val) =>
                          updateFileConfig(index, 'fecha_libro', val)
                        }
                        renderInput={(params) => (
                          <TextField {...params} size='small' fullWidth />
                        )}
                      />
                    </LocalizationProvider>
                  </Stack>
                </Stack>
              )}

              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={2}
                sx={{ mt: 2 }}
              >
                <TextField
                  label='Display Name'
                  size='small'
                  fullWidth
                  value={item.filename}
                  disabled={isActa}
                  onChange={(e) =>
                    updateFileConfig(index, 'filename', e.target.value)
                  }
                  inputProps={{ maxLength: 100 }}
                />

                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    minWidth: '250px'
                  }}
                >
                  <Checkbox
                    checked={item.hasExpiration}
                    onChange={(e) => {
                      const isChecked = e.target.checked
                      updateFileConfig(index, 'hasExpiration', isChecked)
                      if (!isChecked)
                        updateFileConfig(index, 'expiration', null)
                    }}
                  />
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DesktopDatePicker
                      label='Expires'
                      disabled={!item.hasExpiration}
                      value={item.expiration}
                      onChange={(val) => {
                        const { $y: year, $M: month, $D: day } = val
                        const formattedExpiration = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`
                        updateFileConfig(
                          index,
                          'expiration',
                          formattedExpiration
                        )
                      }}
                      renderInput={(params) => (
                        <TextField {...params} size='small' fullWidth />
                      )}
                    />
                  </LocalizationProvider>
                </Box>
              </Stack>

              <TextField
                label='Notes'
                size='small'
                fullWidth
                multiline
                rows={2}
                sx={{ mt: 2 }}
                value={item.notes}
                onChange={(e) =>
                  updateFileConfig(index, 'notes', e.target.value)
                }
                inputProps={{ maxLength: 255 }}
              />

              <FormControlLabel
                sx={{ mt: 1 }}
                control={
                  <Checkbox
                    icon={<VisibilityOff />}
                    checkedIcon={<Visibility color='primary' />}
                    checked={item.visible}
                    onChange={(e) =>
                      updateFileConfig(index, 'visible', e.target.checked)
                    }
                  />
                }
                label={
                  item.visible ? 'Visible to client' : 'Hidden from client'
                }
              />
            </Box>
          ))}

          <Box>
            <FormControlLabel
              control={
                <Checkbox
                  checked={notify}
                  onChange={(e) => setNotify(e.target.checked)}
                />
              }
              label='Notify users about this upload'
            />
            {notify && (
              <Box sx={{ mt: 1 }}>
                <DocAlertRecipientsForm
                  recipients={recipients}
                  suggestions={suggestions}
                  onAdd={handleAddRecipient}
                  onRemove={handleRemoveRecipient}
                />
              </Box>
            )}
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={handleBack} color='inherit'>
          Back
        </Button>
        <Button
          variant='contained'
          disabled={isUploadDisabled || (notify && recipients.length === 0)}
          onClick={() => handleUpload(config, notify, recipients)}
        >
          Confirm & Upload
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default ConfigurationDialog
